import React, { useState } from 'react';
import { ChevronDown, ChevronRight } from 'lucide-react';
import JsonTreeViewer from './JsonTreeViewer';

interface AttributeValueProps {
  value: unknown;
  keyName?: string;
}

const AttributeValue: React.FC<AttributeValueProps> = ({ value, keyName }) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [expandedPaths, setExpandedPaths] = useState<Set<string>>(new Set());

  // Handle boolean values
  if (typeof value === 'boolean') {
    return (
      <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-primary/10 text-primary">
        {value ? 'True' : 'False'}
      </span>
    );
  }

  // Handle null/undefined
  if (value === undefined || value === null) {
    return (
      <span className="text-muted-foreground italic" aria-label="Not available">
        N/A
      </span>
    );
  }

  // Handle arrays
  if (Array.isArray(value)) {
    const preview =
      value.length > 0
        ? `[${value.length} item${value.length > 1 ? 's' : ''}]`
        : '[]';

    const handleTogglePath = (path: string) => {
      setExpandedPaths((prev) => {
        const next = new Set(prev);
        if (next.has(path)) {
          next.delete(path);
        } else {
          next.add(path);
        }
        return next;
      });
    };

    const handleExpand = () => {
      setIsExpanded(true);
      // Initialize root path when expanding
      if (!expandedPaths.has('root')) {
        setExpandedPaths(new Set(['root']));
      }
    };

    return (
      <div className="w-full">
        <button
          onClick={() => {
            if (!isExpanded) {
              handleExpand();
            } else {
              setIsExpanded(false);
            }
          }}
          className="w-full flex items-center justify-between gap-2 group focus:outline-none rounded-md transition-colors"
          aria-label={
            isExpanded
              ? `Collapse array with ${value.length} items`
              : `Expand array with ${value.length} items`
          }
          aria-expanded={isExpanded}
        >
          <div className="text-sm font-medium text-gray-600 min-w-0 flex-shrink-0">
            {keyName}
          </div>
          <div className="flex items-center gap-2">
            <span className="flex-shrink-0 text-gray-400 group-hover:text-gray-600 transition-colors">
              {isExpanded ? (
                <ChevronDown className="w-4 h-4" />
              ) : (
                <ChevronRight className="w-4 h-4" />
              )}
            </span>
            <span className="text-sm font-mono text-gray-600 group-hover:text-gray-900 transition-colors">
              {preview}
            </span>
          </div>
        </button>
        {isExpanded && (
          <div className="mt-2 rounded-lg border border-gray-200 bg-white p-4 shadow-sm overflow-x-auto">
            <JsonTreeViewer
              data={value}
              defaultExpanded={true}
              path="root"
              expandedPaths={expandedPaths}
              onTogglePath={handleTogglePath}
            />
          </div>
        )}
      </div>
    );
  }

  // Handle objects
  if (typeof value === 'object') {
    const keys = Object.keys(value as Record<string, unknown>);
    const preview =
      keys.length > 0
        ? `{${keys.length} key${keys.length > 1 ? 's' : ''}}`
        : '{}';

    const handleTogglePath = (path: string) => {
      setExpandedPaths((prev) => {
        const next = new Set(prev);
        if (next.has(path)) {
          next.delete(path);
        } else {
          next.add(path);
        }
        return next;
      });
    };

    const handleExpand = () => {
      setIsExpanded(true);
      // Initialize root path when expanding
      if (!expandedPaths.has('root')) {
        setExpandedPaths(new Set(['root']));
      }
    };

    return (
      <div className="w-full">
        <button
          onClick={() => {
            if (!isExpanded) {
              handleExpand();
            } else {
              setIsExpanded(false);
            }
          }}
          className="w-full flex items-center justify-between gap-2 group focus:outline-none rounded-md transition-colors"
          aria-label={
            isExpanded
              ? `Collapse object with ${keys.length} keys`
              : `Expand object with ${keys.length} keys`
          }
          aria-expanded={isExpanded}
        >
          <div className="text-sm font-medium text-gray-600 min-w-0 flex-shrink-0">
            {keyName}
          </div>
          <div className="flex items-center gap-2">
            <span className="flex-shrink-0 text-gray-400 group-hover:text-gray-600 transition-colors">
              {isExpanded ? (
                <ChevronDown className="w-4 h-4" />
              ) : (
                <ChevronRight className="w-4 h-4" />
              )}
            </span>
            <span className="text-sm font-mono text-gray-600 group-hover:text-gray-900 transition-colors">
              {preview}
            </span>
          </div>
        </button>
        {isExpanded && (
          <div className="mt-2 rounded-lg border border-gray-200 bg-white p-4 shadow-sm overflow-x-auto">
            <JsonTreeViewer
              data={value}
              defaultExpanded={true}
              path="root"
              expandedPaths={expandedPaths}
              onTogglePath={handleTogglePath}
            />
          </div>
        )}
      </div>
    );
  }

  // Handle primitive values (string, number, etc.)
  const stringValue = String(value);
  const isLongValue = stringValue.length > 50;

  return (
    <span
      className="text-gray-900 break-words"
      title={isLongValue ? stringValue : undefined}
    >
      {stringValue}
    </span>
  );
};

export default AttributeValue;
